/* eslint-disable react-compiler/react-compiler */
import { useGetSignedPhotoQuery } from '@core/redux/query';
import { deviceManagementApiV1 as deviceManagementApi } from '@deepup/apis';
import type { Photo } from '@photos/types';
import { BoundingBox } from '@viewer3D/types';
import { proxy, type Snapshot as ValtioSnapshot } from 'valtio';
import { useCallback } from 'react';

export type Snapshot<T> = ValtioSnapshot<T>;

export type Category = 'ALL' | string;

export const possibleClassifications = ['Ruler', 'All others'] as const;
export type Classification = (typeof possibleClassifications)[number];
export type Scanner = deviceManagementApi.Scanner;

export interface Photos {
  showPhotos: boolean;
  showPreview: boolean;
  selectedPhoto: Photo | null;
  previewPhotos: Photo[];
  allPhotos: Photo[];
  filteredPhotosByDateOrBbox: Photo[];
  singles: Photo[];
  clusters: Photo[][];
  selectedCategories: Category[];
  possibleCategories: Category[];
  possibleClassifications: Classification[];
  selectedClassifications: Classification[];
  possibleScanners: Scanner[];
  selectedScanners: Scanner[];
  currentPhotoIndex: number;
  isLoading: boolean;
  previewPosition: { x: number; y: number } | null;
  hoverPreviewPosition: { x: number; y: number } | null;
  isHoveringIcon: boolean;
  isHoveringPreview: boolean;
  selectedPhotos: Photo[];
}

export const photoSelectionBoundingBoxState = proxy<{ boundingBox: BoundingBox }>({
  boundingBox: {
    minX: 0,
    minY: 0,
    maxX: 0,
    maxY: 0,
  },
});

export const setPhotoSelectionBoundingBox = (boundingBox: BoundingBox): void => {
  photoSelectionBoundingBoxState.boundingBox = boundingBox;
};

export const photosState = proxy<Photos>({
  showPhotos: false,
  showPreview: false,
  selectedPhoto: null,
  selectedScanners: [],
  previewPhotos: [],
  allPhotos: [],
  filteredPhotosByDateOrBbox: [],
  singles: [],
  clusters: [],
  currentPhotoIndex: 0,
  isLoading: false,
  selectedCategories: [],
  possibleCategories: [],
  possibleClassifications: [...possibleClassifications],
  selectedClassifications: [...possibleClassifications],
  possibleScanners: [],
  previewPosition: null,
  hoverPreviewPosition: null,
  isHoveringIcon: false,
  isHoveringPreview: false,
  selectedPhotos: [],
});

export const setSelectedPhotos = (photos: Photo[]): void => {
  photosState.selectedPhotos = photos;
};

export const reduceSelectedPhotos = (photoIds: string[] = []): void => {
  photosState.selectedPhotos = photoIds
    .map((id) => photosState.selectedPhotos.find((photo) => photo.id === id))
    .filter(Boolean) as Photo[];
};

export const togglePreview = (show = !photosState.showPreview): void => {
  photosState.showPreview = show;
};

export const setSelectedPhoto = (photo: Photo | null): void => {
  photosState.selectedPhoto = photo;
};

export const setPhotoCategories = (categories: Category[]): void => {
  photosState.possibleCategories = [...categories.sort()];
};

export const setPossibleScanners = (scanners: Scanner[]) => {
  photosState.possibleScanners = [...scanners.sort()];
};

export const setSelectedCategories = (categories: Category[]): void => {
  photosState.selectedCategories = categories;
};

export const setSelectedClassifications = (classifications: Classification[]): void => {
  photosState.selectedClassifications = classifications;
};

export const setSelectedScanners = (categories: Scanner[]): void => {
  photosState.selectedScanners = categories;
};

export const areAllCategoriesSelected = (categories: readonly Category[]): boolean =>
  categories.length === photosState.possibleCategories.length;

export const areAllClassificationsSelected = (
  classifications: readonly Classification[],
): boolean => classifications.length === photosState.possibleClassifications.length;

export const areAllScannersSelected = (scanners: readonly Scanner[]): boolean =>
  scanners.length === photosState.possibleScanners.length;

export const setAllPhotos = (photos: Photo[]): void => {
  photosState.allPhotos = photos;
  photosState.singles = [];
  photosState.clusters = [];
};

export const togglePhotos = (show = !photosState.showPhotos): void => {
  photosState.showPhotos = show;
};

export const useSetPreviewPhotos = () => {
  const [getSignedPhoto] = useGetSignedPhotoQuery();

  const setPreviewPhotos = useCallback(
    async (photos: readonly Snapshot<Photo>[] | readonly Photo[]): Promise<void> => {
      photosState.currentPhotoIndex = 0;
      if (!photos?.length) {
        photosState.previewPhotos = [];
      } else {
        photosState.isLoading = true;
        photosState.previewPhotos = await Promise.all(
          photos.map(async (photo) => {
            const { data } = await getSignedPhoto({ photoId: photo.id }, true);
            return {
              ...(photo as Photo),
              url: data?.url ?? photo.url,
            };
          }),
        );
        photosState.isLoading = false;
      }
    },
    [getSignedPhoto],
  );

  return { setPreviewPhotos };
};

export const showNextPhoto = (): void => {
  const photosToDisplay = photosState.previewPhotos;
  if (photosToDisplay.length === 0) return;

  photosState.currentPhotoIndex = (photosState.currentPhotoIndex + 1) % photosToDisplay.length;

  if (
    photosState.selectedPhoto !== null &&
    photosState.previewPhotos.find((p) => p.id === photosState.selectedPhoto?.id)
  ) {
    const newSelectedPhoto = photosToDisplay[photosState.currentPhotoIndex];
    if (photosState.selectedPhoto.id !== newSelectedPhoto.id) {
      photosState.selectedPhoto = newSelectedPhoto;
    }
  }
};

export const openCurrentPhotoInNewTab = (): void => {
  if (!photosState.previewPhotos.length) return;
  window.open(photosState.previewPhotos[photosState.currentPhotoIndex].url, '_blank');
};

export const openPhotoInNewTab = (photo: { readonly url: string }): void => {
  if (!photo.url) {
    console.error('Photo URL is not available.');
    return;
  }
  window.open(photo.url, '_blank');
};

export const setPreviewPosition = (position: { x: number; y: number } | null): void => {
  photosState.previewPosition = position;
};

export const setIsHoveringIcon = (isHovering: boolean): void => {
  photosState.isHoveringIcon = isHovering;
  photosState.showPreview = isHovering || photosState.isHoveringPreview;
};

export const setIsHoveringPreview = (isHovering: boolean): void => {
  photosState.isHoveringPreview = isHovering;
  photosState.showPreview = isHovering || photosState.isHoveringIcon;
};

export const clearPreviewPhotos = () => {
  photosState.previewPhotos = [];
  photosState.previewPosition = null;
  photosState.currentPhotoIndex = 0;
};

export const updatePhotoInPhotosState = (updatedPhoto: Photo): void => {
  photosState.singles = photosState.singles.map((p) =>
    p.id === updatedPhoto.id ? updatedPhoto : p,
  );
};

export const updatePhotoEverywhere = (updatedPhoto: Photo): void => {
  photosState.singles = photosState.singles.map((p) =>
    p.id === updatedPhoto.id ? updatedPhoto : p,
  );

  photosState.clusters = photosState.clusters.map((cluster) =>
    cluster.map((p) => (p.id === updatedPhoto.id ? updatedPhoto : p)),
  );

  photosState.previewPhotos = photosState.previewPhotos.map((p) =>
    p.id === updatedPhoto.id ? { ...updatedPhoto, url: p.url } : p,
  );

  if (photosState.selectedPhoto?.id === updatedPhoto.id) {
    photosState.selectedPhoto = {
      ...updatedPhoto,
      url: photosState.selectedPhoto.url,
    };
  }
};
